import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { QRCodeData, QRDesignSettings } from '@/stores/qrStore';
import { toast } from 'sonner';
import { Json } from '@/integrations/supabase/types';
import { generateSlug, getRedirectUrl } from '@/lib/dynamic-qr-utils';
import { generateQRContent } from '@/lib/qr-utils';
import { useSubscription } from '@/hooks/useSubscription';

interface DBQRCode {
  id: string;
  user_id: string;
  name: string;
  type: string;
  content_data: Json;
  design_settings: Json;
  is_dynamic: boolean;
  created_at: string;
  updated_at: string;
  short_slug: string | null;
  redirect_url: string | null;
  is_active: boolean;
  clicks_count: number;
  last_clicked_at: string | null;
  routing_rules: Json;
  analytics_config: Json;
  blockchain_config: Json;
}

export interface QRCodeWithDynamic extends QRCodeData {
  id: string;
  createdAt: string;
  updatedAt: string;
  shortSlug?: string;
  redirectUrl?: string;
  isActive: boolean;
  clicksCount: number;
  lastClickedAt?: string;
  routingRules: Array<{
    id: string;
    type: 'time' | 'os' | 'device' | 'country' | 'language';
    condition: Record<string, unknown>;
    destination: string;
  }>;
}

const defaultDesignSettings: QRDesignSettings = {
  dotsColor: '#1a1a2e',
  backgroundColor: '#ffffff',
  backgroundTransparent: false,
  gradient: {
    enabled: false,
    type: 'linear',
    color1: '#ffffff',
    color2: '#f0f0f0',
    rotation: 0
  },
  cornersSquareColor: '#1a1a2e',
  cornersDotColor: '#1a1a2e',
  dotsType: 'rounded',
  cornersSquareType: 'extra-rounded',
  cornersDotType: 'dot',
  logo: {
    enabled: false,
    url: '',
    size: 25,
    transparentBackground: true
  },
  frame: {
    style: 'none',
    text: 'SCAN ME',
    color: '#1a1a2e',
    thickness: 20,
    textPosition: 'bottom'
  },
  errorCorrectionLevel: 'M',
  width: 300,
  height: 300,
  margin: 10,
  downloadFormat: 'png',
  resolution: 1000
};

function mapDBToQRCode(dbQR: DBQRCode): QRCodeWithDynamic {
  const storedSettings = dbQR.design_settings as unknown as Partial<QRDesignSettings>;
  
  return {
    id: dbQR.id,
    name: dbQR.name,
    type: dbQR.type as QRCodeData['type'],
    contentData: (dbQR.content_data as Record<string, string>) || {},
    designSettings: {
      ...defaultDesignSettings,
      ...storedSettings,
      gradient: {
        ...defaultDesignSettings.gradient,
        ...(storedSettings?.gradient || {})
      },
      logo: {
        ...defaultDesignSettings.logo,
        ...(storedSettings?.logo || {})
      },
      frame: {
        ...defaultDesignSettings.frame,
        ...(storedSettings?.frame || {})
      }
    },
    isDynamic: dbQR.is_dynamic,
    createdAt: dbQR.created_at,
    updatedAt: dbQR.updated_at,
    shortSlug: dbQR.short_slug || undefined,
    redirectUrl: dbQR.redirect_url || undefined,
    isActive: dbQR.is_active,
    clicksCount: dbQR.clicks_count,
    lastClickedAt: dbQR.last_clicked_at || undefined,
    routingRules: (dbQR.routing_rules as any) || [],
    analyticsConfig: (dbQR.analytics_config as any) || {},
    blockchainConfig: (dbQR.blockchain_config as any) || {},
  };
}

export function useQRCodes() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const { incrementUsage, isAtDynamicQRLimit, isAtStaticQRLimit, limits } = useSubscription();

  const { data: qrCodes = [], isLoading } = useQuery({
    queryKey: ['qr-codes', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('qr_codes')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      return (data as any as DBQRCode[]).map(mapDBToQRCode);
    },
    enabled: !!user,
  });

  const saveQRCode = useMutation({
    mutationFn: async (qr: QRCodeData) => {
      if (!user) throw new Error('Not authenticated');
      
      // If QR already has an ID, it was previously saved — return existing record
      if (qr.id) {
        const { data: existing, error: fetchErr } = await supabase
          .from('qr_codes')
          .select()
          .eq('id', qr.id)
          .single();
        if (!fetchErr && existing) {
          return mapDBToQRCode(existing as any as DBQRCode);
        }
      }
      
      // Check limit for dynamic QR codes
      if (qr.isDynamic && isAtDynamicQRLimit()) {
        throw new Error(`You've reached your dynamic QR code limit (${limits.dynamicQRLimit}). Please upgrade your plan.`);
      }
      
      let shortSlug: string | null = null;
      let redirectUrl: string | null = null;
      
      // Generate unique slug for dynamic QR codes
      if (qr.isDynamic) {
        let isUnique = false;
        let attempts = 0;
        
        while (!isUnique && attempts < 10) {
          shortSlug = generateSlug();
          const { data: existing } = await supabase
            .from('qr_codes')
            .select('id')
            .eq('short_slug', shortSlug)
            .maybeSingle();
          
          if (!existing) {
            isUnique = true;
          }
          attempts++;
        }
        
        if (!isUnique) {
          throw new Error('Failed to generate unique slug');
        }
        
        redirectUrl = getRedirectUrl(shortSlug);
      }
      
      const { data, error } = await supabase
        .from('qr_codes')
        .insert({
          user_id: user.id,
          name: qr.name,
          type: qr.type,
          content_data: qr.contentData as Json,
          design_settings: qr.designSettings as unknown as Json,
          is_dynamic: qr.isDynamic,
          short_slug: shortSlug,
          redirect_url: redirectUrl,
          routing_rules: qr.routingRules as unknown as Json || [],
          analytics_config: qr.analyticsConfig as unknown as Json || {},
          blockchain_config: qr.blockchainConfig as unknown as Json || {},
        })
        .select()
        .single();
        
      if (error) throw error;
      
      // Increment usage counter for dynamic QR codes
      if (qr.isDynamic) {
        incrementUsage({ field: 'dynamic_qr_count', amount: 1 });
      }
      
      // Trigger URL security scan in background for URL-based types
      const urlTypes = ['url', 'whatsapp', 'email', 'instagram', 'twitter', 'linkedin', 'paypal'];
      if (urlTypes.includes(qr.type)) {
        const contentString = typeof qr.contentData === 'string' 
          ? qr.contentData 
          : JSON.stringify(qr.contentData);
        
        fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scan-url-security`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            qr_code_id: data.id,
            url: contentString,
          }),
        }).catch(err => console.error('URL security scan failed:', err));
      }
      
      return mapDBToQRCode(data as any as DBQRCode);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qr-codes'] });
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      queryClient.invalidateQueries({ queryKey: ['usage'] });
      toast.success('QR code saved successfully!');
    },
    onError: (error) => {
      toast.error('Failed to save QR code: ' + error.message);
    },
  });

  const deleteQRCode = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('qr_codes')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qr-codes'] });
      toast.success('QR code deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete: ' + error.message);
    },
  });

  const updateQRCode = useMutation({
    mutationFn: async ({ id, ...qr }: QRCodeData & { id: string }) => {
      const { error } = await supabase
        .from('qr_codes')
        .update({
          name: qr.name,
          type: qr.type,
          content_data: qr.contentData as Json,
          design_settings: qr.designSettings as unknown as Json,
          is_dynamic: qr.isDynamic,
          analytics_config: qr.analyticsConfig as unknown as Json || {},
          blockchain_config: qr.blockchainConfig as unknown as Json || {},
          routing_rules: qr.routingRules as unknown as Json || [],
        })
        .eq('id', id);
        
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qr-codes'] });
      toast.success('QR code updated!');
    },
    onError: (error) => {
      toast.error('Failed to update: ' + error.message);
    },
  });

  // Update dynamic QR content (for edit modal)
  const updateDynamicQR = useMutation({
    mutationFn: async ({ 
      id, 
      contentData, 
      isActive, 
      analyticsConfig,
      blockchainConfig,
      routingRules
    }: { 
      id: string; 
      contentData: Record<string, string>; 
      isActive: boolean;
      analyticsConfig?: any;
      blockchainConfig?: any;
      routingRules?: any[];
    }) => {
      // Also regenerate redirect_url from updated content
      const existing = qrCodes.find(q => q.id === id);
      const redirectUrl = existing?.type
        ? generateQRContent(existing.type as any, contentData)
        : undefined;

      const { error } = await supabase
        .from('qr_codes')
        .update({
          content_data: contentData as Json,
          is_active: isActive,
          analytics_config: analyticsConfig as Json,
          blockchain_config: blockchainConfig as Json,
          routing_rules: routingRules as Json,
          ...(redirectUrl ? { redirect_url: redirectUrl } : {}),
        })
        .eq('id', id);
        
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qr-codes'] });
    },
    onError: (error) => {
      toast.error('Failed to update: ' + error.message);
    },
  });

  // Rename a QR code
  const renameQRCode = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { error } = await supabase
        .from('qr_codes')
        .update({ name })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qr-codes'] });
      toast.success('QR code renamed!');
    },
    onError: (error) => {
      toast.error('Failed to rename: ' + error.message);
    },
  });

  // Count dynamic QR codes
  const dynamicCount = qrCodes.filter(qr => qr.isDynamic).length;

  return {
    qrCodes,
    isLoading,
    saveQRCode,
    deleteQRCode,
    updateQRCode,
    updateDynamicQR,
    renameQRCode,
    dynamicCount,
  };
}
